import { useCallback } from 'react';

import { NoteFileForm } from '../../../components/Forms/NoteFileForm';
import type FileInterface from '../../../interfaces/File';
import type { UseFilesActionsDeps } from '../deps';
import type { UseFilesActionsEvents, UseFilesActionsReturn } from '../types';

/** Safety net: stop waiting to open a freshly created note if its upload never reports back. */
const AUTO_OPEN_TIMEOUT_MS = 30000;

/**
 * Provides the modal flow and creation logic for note files (`.fgnote`, sanitized HTML content).
 */
export function useFilesNoteActions(
  deps: UseFilesActionsDeps,
): Pick<UseFilesActionsReturn, 'handleCreateNoteFile'> {
  const {
    folderUidRef,
    uploadFiles,
    onNewFileUploaded,
    offNewFileUploaded,
    modal,
    notification,
    message,
    t,
    isAdminOrContributor,
  } = deps;

  /**
   * Create and upload a new empty note as a `.fgnote` file (sanitized HTML) in the current folder,
   * then open it in the viewer as soon as the upload completes.
   */
  const createNoteFile = useCallback(
    // Default to a single space: a 0-byte file is rejected by the upload pipeline
    (name: string, content = ' ') => {
      try {
        const fileName = `${name}.fgnote`;
        const file = new File([content], fileName, { type: 'text/html' });

        // One-shot listener: when this note's upload completes, open it in a new window.
        let timeoutId: ReturnType<typeof setTimeout>;
        const handleUploaded = (uploaded: FileInterface) => {
          if (uploaded?.fileName !== fileName) return;
          clearTimeout(timeoutId);
          offNewFileUploaded(handleUploaded);
          window.open(`/viewer/${uploaded.id}`, '_blank', 'noopener,noreferrer')?.focus();
        };
        onNewFileUploaded(handleUploaded);
        timeoutId = setTimeout(() => offNewFileUploaded(handleUploaded), AUTO_OPEN_TIMEOUT_MS);

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);

        uploadFiles(
          dataTransfer.files,
          true,
          folderUidRef.current ? parseInt(folderUidRef.current) : undefined,
        );

        message.success({
          content: t('notifications.fileCreated', { resource: fileName }),
        });
      } catch (err) {
        console.error('[createNoteFile] Error:', err);
        notification.error({
          title: t('errors.genericCreateError', { resource: t('common.file') }),
        });
      }
    },
    [
      folderUidRef,
      t,
      notification,
      uploadFiles,
      onNewFileUploaded,
      offNewFileUploaded,
      message,
    ],
  );

  /**
   * Show a modal to create a new markdown note, if permitted.
   */
  const handleCreateNoteFile = useCallback(
    (_events?: UseFilesActionsEvents) => {
      if (!isAdminOrContributor) {
        notification.error({
          title: t('errors.noUploadPermission'),
        });
        return;
      }

      const modalInstance = modal.confirm({
        title: t('note.createTitle'),
        width: 480,
        content: (
          <NoteFileForm
            onSubmit={(name) => {
              createNoteFile(name);
              modalInstance.destroy();
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
      });
    },
    [modal, t, notification, createNoteFile, isAdminOrContributor],
  );

  return {
    handleCreateNoteFile,
  };
}
